const UTM_KEY_PATTERN = /^utm_/i;
const MAX_UTM_PARAMS = 5;
const STORAGE_PREFIX = "sum:utm:";

export type UtmParams = Record<string, string>;

const storageKey = (processId: string) => `${STORAGE_PREFIX}${processId}`;

export const extractUtmParamsFromSearch = (search: string): UtmParams => {
  const params = new URLSearchParams(search);
  const result: UtmParams = {};
  let count = 0;
  params.forEach((value, rawKey) => {
    if (count >= MAX_UTM_PARAMS) return;
    if (!UTM_KEY_PATTERN.test(rawKey)) return;
    const key = rawKey.toLowerCase();
    if (key in result) return;
    result[key] = value;
    count += 1;
  });
  return result;
};

/** UTM parameters in the address replace the kept set as a whole; an address without any keeps it. */
export const captureUtmParams = (
  processId: string,
  search: string
): UtmParams => {
  if (!processId) return {};
  const extracted = extractUtmParamsFromSearch(search);
  if (Object.keys(extracted).length > 0) {
    try {
      sessionStorage.setItem(storageKey(processId), JSON.stringify(extracted));
    } catch {}
    return extracted;
  }
  return getStoredUtmParams(processId);
};

export const getStoredUtmParams = (processId: string): UtmParams => {
  if (!processId) return {};
  try {
    const raw = sessionStorage.getItem(storageKey(processId));
    return raw ? (JSON.parse(raw) as UtmParams) : {};
  } catch {
    return {};
  }
};

export const appendUtmParamsToUrl = (url: URL, utm: UtmParams): void => {
  Object.entries(utm).forEach(([key, value]) => {
    if (url.searchParams.has(key)) return;
    url.searchParams.append(key, value);
  });
};

/** The UTM parameters of a visit to an entity's booking pages, kept like the form's. */
export const captureBookingUtmParams = (
  entityId: string,
  search: string
): UtmParams => captureUtmParams(`booking:${entityId}`, search);

const ABSOLUTE_URL = /^([a-z][a-z\d+.-]*:|\/\/)/i;

/** `href` with the UTM parameters it does not carry yet; a relative address stays relative. */
export const withUtmParams = (href: string, utm: UtmParams): string => {
  if (Object.keys(utm).length === 0) return href;
  const url = new URL(href, window.location.origin);
  appendUtmParamsToUrl(url, utm);
  return ABSOLUTE_URL.test(href)
    ? url.toString()
    : `${url.pathname}${url.search}${url.hash}`;
};
